import GhHtmlElement from '@gudhub/gh-html-element';
import { GUDHUB_SETTINGS } from '../../../gudhub_settings.js';
import { sipRegistrationMixin } from './mixins/sipRegistrationMixin.js';
import { callSessionMixin } from './mixins/callSessionMixin.js';
import { dialogMixin } from './mixins/dialogMixin.js';
import { sipEventsMixin } from './mixins/sipEventsMixin.js';
import { nativeCallsMixin } from './mixins/nativeCallsMixin.js';
import { nativeEngineMixin } from './mixins/nativeEngineMixin.js';
import './calls.scss';

class GhCalls extends GhHtmlElement {

    constructor() {
        super();
        this.callState = 'idle';
        this.sipEnabled = false;
        this.session = null;
        this.userAgent = null;
        this.registerer = null;
        this.handleSipEvent = this.handleSipEvent.bind(this);
    }

    //********************* CONNECTED CALLBACK ********************************//
    // <gh-calls> is a global, attribute-less element (like <gudhub-messenger>) -
    // the base class's attributeChangedCallback never calls onInit(), so call it
    // directly here instead, the same way GudhubMessenger does.
    connectedCallback() {
        if(this._callsInitialized) return;
        this._callsInitialized = true;

        this.waitForGudhub().then(() => this.onInit()).catch((err) => {
            console.error('GhCalls init failed', err);
        });
    }

    //********************* WAIT FOR GUDHUB ********************************//
    waitForGudhub() {
        if(window.gudhub) return Promise.resolve();

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if(window.gudhub) {
                    clearInterval(interval);
                    resolve();
                }
            }, 50);
        });
    }

    //********************* ON INIT ********************************//
    async onInit() {
        if(!GUDHUB_SETTINGS.sip_server_url) {
            this.sipEnabled = false;
            return;
        }

        // <gh-calls> now mounts unconditionally at page load (see index.html's
        // own comment) rather than only when the messenger panel is shown, so
        // this can genuinely run before any user is authenticated (e.g. on
        // /login, /signup). waitForActiveUserId's 10s poll then resolves
        // undefined in that case - skip registration entirely rather than
        // attempting one as the literal string "undefined".
        const activeUserId = await this.waitForActiveUserId();
        if(!activeUserId) {
            this.sipEnabled = false;
            return;
        }
        this.activeUserId = String(activeUserId);
        this.toast = gudhub.ghconstructor.angularInjector.get('ghToastService');

        // Registers this device's push token so the SIP server can wake the
        // phone for a call that arrives while the app isn't running, and
        // wires the native call screen's buttons to the SIP session. No-ops
        // in a browser. Returns once it knows how this tab should connect;
        // with a native call engine the rest carries on in the background.
        await this.initNativeCalls();

        // The first registerSip() attempt races a real network round trip
        // (WSS handshake + REGISTER + digest challenge retry) against a
        // fixed client-side timeout (see waitForRegistered) - a single slow
        // attempt (e.g. right after a FreeSWITCH restart, or ordinary
        // network jitter) can lose that race even though the server goes on
        // to register this tab moments later (confirmed live: FreeSWITCH
        // logged a clean register_attempt -> register for this exact
        // session while this tab's own attempt had already timed out and
        // given up). Unlike handleTransportDisconnect (which only runs
        // after an already-successful first registration), this path had no
        // retry of its own - one lost race permanently stranded the tab
        // with no way to recover short of a manual reload. Same bounded
        // retry as handleTransportDisconnect (30 x 10s = 5 minutes).
        for(let attempt = 1; attempt <= 30; attempt++) {
            try {
                await this.registerSip();
                this.sipEnabled = true;
                this.subscribeToSipEvents();
                this.startHeartbeat();
                return;
            } catch (err) {
                console.error(`SIP registration attempt ${attempt} failed`, err);
                await new Promise((resolve) => setTimeout(resolve, 10000));
            }
        }

        this.sipEnabled = false;
        this.toast?.customToast({
            template: `<md-toast><p>Calling connection could not be established - try reloading the page.</p></md-toast>`,
            hideDelay: 8000
        });
    }

    //********************* WAIT FOR ACTIVE USER ID ********************************//
    // Same polling pattern as gudhub-messenger's waitForActiveUserId -
    // gudhub.storage.user.user_id may not be resolved yet when this element connects.
    async waitForActiveUserId() {
        if(gudhub.storage.user?.user_id) return gudhub.storage.user.user_id;

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if(gudhub.storage.user?.user_id) {
                    clearInterval(interval);
                    resolve(gudhub.storage.user.user_id);
                }
            }, 100);

            setTimeout(() => {
                clearInterval(interval);
                resolve(gudhub.storage.user?.user_id);
            }, 10000);
        });
    }

    disconnectedCallback() {
        this.stopHeartbeat();
        this.teardownCall();
        this.unsubscribeFromSipEvents();
        this.unregisterSip();
    }
}

Object.assign(GhCalls.prototype,
    sipRegistrationMixin,
    callSessionMixin,
    dialogMixin,
    sipEventsMixin,
    // Inert outside the Capacitor shells - every method returns early when
    // window.Capacitor is absent, so the browser build is unaffected.
    nativeCallsMixin,
    nativeEngineMixin,
);

if(!customElements.get('gh-calls')){
    customElements.define('gh-calls', GhCalls);
}
